import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { GraduationCap, PlayCircle, BookOpen } from 'lucide-react'
import { courseApi, type CourseEnrollmentItem, type CourseProgress } from '../api/lessonBuilderApi'

type Row = CourseEnrollmentItem & { progress?: CourseProgress | null }

// Trang "Đang học" của học viên: các khoá đã ghi danh + tiến độ + tiếp tục học.
const MyLearningPage: React.FC = () => {
  const navigate = useNavigate()
  const [rows, setRows] = useState<Row[] | null>(null)

  useEffect(() => {
    courseApi.myEnrollments().then(async (list) => {
      const withProg = await Promise.all(
        list.map(async (e) => ({ ...e, progress: await courseApi.progress(e.course_id) }))
      )
      setRows(withProg)
    }).catch(() => setRows([]))
  }, [])

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="flex items-center gap-2 text-emerald-100 text-sm font-semibold mb-2">
            <GraduationCap size={18} /> Học tập của tôi
          </div>
          <h1 className="text-3xl font-black">Khoá học đang học</h1>
          <p className="text-emerald-100 mt-1">Tiếp tục từ chỗ bạn đang dừng.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 md:p-6">
        {rows === null ? (
          <div className="py-20 text-center text-gray-400">Đang tải...</div>
        ) : rows.length === 0 ? (
          <div className="py-20 text-center">
            <BookOpen size={42} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-400 mb-3">Bạn chưa ghi danh khoá học nào.</p>
            <Link to="/khoa-hoc" className="text-blue-600 font-semibold">Khám phá khoá học →</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => {
              const p = r.progress
              const pct = p?.percent ?? 0
              return (
                <div key={r.course_id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
                  <div className="w-20 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0 overflow-hidden">
                    {r.coverImage ? <img src={r.coverImage} alt={r.title} className="w-full h-full object-cover" /> : <GraduationCap className="text-white/80" size={22} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 truncate">{r.title}</h3>
                    {p && (
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden max-w-[260px]">
                          <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs font-bold text-gray-500">{p.completed}/{p.total} · {pct}%</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => navigate(`/bai-giang/${p?.nextSlug || ''}` )}
                    disabled={!p?.nextSlug}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-40 shrink-0"
                  >
                    <PlayCircle size={18} /> {pct >= 100 ? 'Học lại' : 'Học tiếp'}
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default MyLearningPage
